import { CaretRight, Folder } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import type { Collection } from "~/api/types";
import { cn } from "~/foundation/utils/cn";

export interface CollectionTreeProps {
  /** Already filtered to what this tree shows; sorted here. */
  collections: Collection[];
  pathname: string;
  /** Mobile drawer closes on navigate; the desktop sidebar does nothing. */
  onNavigate?: (() => void) | undefined;
}

export const TREE_ROW_CLASS =
  "flex h-8 min-w-0 flex-1 items-center gap-2 rounded-sm px-1.5 text-body text-sidebar-text " +
  "transition-colors duration-100 ease-in-out hover:bg-hover-bg motion-reduce:transition-none";

export const TREE_ROW_ACTIVE_CLASS = "bg-sidebar-active-bg text-text hover:bg-sidebar-active-bg";

/** Index order first (the workspace's own ordering), then name. */
export function sortCollections(collections: Collection[]): Collection[] {
  return [...collections].sort((a, b) => a.index - b.index || a.name.localeCompare(b.name));
}

/**
 * The disclosure slot every tree row starts with. A node with nothing under it gets the same
 * width as an empty spacer, so names line up and no caret promises contents that are not there.
 */
export function TreeCaret({
  expanded,
  controls,
  name,
  onToggle,
}: {
  expanded: boolean;
  controls: string;
  name: string;
  onToggle: (() => void) | null;
}) {
  const { t } = useTranslation();
  if (!onToggle) {
    return <span aria-hidden className="w-6 shrink-0" />;
  }
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      aria-controls={controls}
      aria-label={expanded ? t("Collapse {{name}}", { name }) : t("Expand {{name}}", { name })}
      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-sm text-text-tertiary hover:bg-hover-bg hover:text-text"
    >
      <CaretRight
        size={12}
        aria-hidden
        className={cn(
          "transition-transform duration-100 motion-reduce:transition-none",
          expanded ? "rotate-90" : "rtl:rotate-180",
        )}
      />
    </button>
  );
}

/** Collection destinations. Document browsing lives under Documents. */
export function CollectionTree({ collections, pathname, onNavigate }: CollectionTreeProps) {
  return (
    <ul className="flex flex-col gap-0.5">
      {sortCollections(collections).map((collection) => (
        <CollectionNode
          key={collection.id}
          collection={collection}
          pathname={pathname}
          onNavigate={onNavigate}
        />
      ))}
    </ul>
  );
}

/** One collection row — shared by the Collections section and the project tree. */
export function CollectionNode({
  collection,
  pathname,
  onNavigate,
}: {
  collection: Collection;
  pathname: string;
  onNavigate?: (() => void) | undefined;
}) {
  const isActive =
    pathname === `/collections/${collection.id}` ||
    pathname.startsWith(`/collections/${collection.id}/`);

  return (
    <li>
      <div className="flex items-center gap-0.5">
        <Link
          to="/collections/$collectionId"
          params={{ collectionId: collection.id }}
          onClick={onNavigate}
          aria-current={isActive ? "page" : undefined}
          className={cn(TREE_ROW_CLASS, isActive && TREE_ROW_ACTIVE_CLASS)}
        >
          {collection.emoji ? (
            <span aria-hidden className="w-4 shrink-0 text-center text-small leading-none">
              {collection.emoji}
            </span>
          ) : (
            <Folder size={16} aria-hidden className="shrink-0 text-text-tertiary" />
          )}
          <span className="truncate">{collection.name}</span>
        </Link>
      </div>
    </li>
  );
}
